import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  LinearProgress, Alert, IconButton, Tooltip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup,
  Avatar,
} from '@mui/material';
import { HomeWork, Add, Close, CheckCircle, Cancel, Home, Laptop } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  pending:   { color: 'warning', label: 'Pending' },
  approved:  { color: 'success', label: 'Approved' },
  rejected:  { color: 'error',   label: 'Rejected' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

const SESSION_LABELS: Record<string, string> = { full_day: 'Full Day', am: 'Morning', pm: 'Afternoon' };

function fmtDate(s?: string) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-HK', { weekday: 'short', day: 'numeric', month: 'short' });
}

interface WfhReq { id: string; date: string; session: string; status: string; reason?: string; location?: string; approverNote?: string; approvedAt?: string; employee?: { id: string; firstName: string; lastName: string; department?: { name: string } }; approver?: { firstName: string; lastName: string }; }
interface WeekDay { date: string; requests: WfhReq[]; count: number; }
interface Stats { todayWfh: number; thisWeekWfh: number; pendingApprovals: number; thisMonthTotal: number; }
interface Policy { id?: string; maxDaysPerWeek: number; maxConsecutiveDays: number; advanceNoticeDays: number; autoApprove: boolean; notes?: string; }

export default function WfhPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = isHR || user?.role === 'manager';
  const today = new Date().toISOString().slice(0, 10);

  const [tab, setTab] = useState(0);
  const [myRequests, setMyRequests] = useState<WfhReq[]>([]);
  const [allRequests, setAllRequests] = useState<WfhReq[]>([]);
  const [weekData, setWeekData] = useState<WeekDay[]>([]);
  const [todayData, setTodayData] = useState<WfhReq[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [requestDialog, setRequestDialog] = useState<{ date: string; session: string; reason: string; location: string } | null>(null);
  const [approveDialog, setApproveDialog] = useState<{ req: WfhReq; note: string; action: 'approve' | 'reject' } | null>(null);
  const [policyDialog, setPolicyDialog] = useState<Partial<Policy> | null>(null);

  const getMondayOfWeek = () => {
    const d = new Date(); d.setDate(d.getDate() - (d.getDay() || 7) + 1);
    return d.toISOString().slice(0, 10);
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [my, td, wk, st, pol] = await Promise.all([
        apiFetch('/api/wfh/my'),
        apiFetch('/api/wfh/today'),
        apiFetch(`/api/wfh/week?weekStart=${getMondayOfWeek()}`),
        apiFetch('/api/wfh/stats'),
        apiFetch('/api/wfh/policy'),
      ]);
      setMyRequests(my);
      setTodayData(td.wfh ?? []);
      setWeekData(wk);
      setStats(st);
      setPolicy(pol);
    } catch (e: any) { setError(e.message); }

    if (isManager) {
      try {
        const pending = await apiFetch('/api/wfh?status=pending');
        setAllRequests(pending);
      } catch {}
    }
    setLoading(false);
  }, [isManager]);

  useEffect(() => { load(); }, [load]);

  const submitRequest = async () => {
    if (!requestDialog) return;
    try {
      await apiFetch('/api/wfh/submit', { method: 'POST', body: JSON.stringify(requestDialog) });
      setRequestDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const handleApproval = async () => {
    if (!approveDialog) return;
    try {
      const ep = approveDialog.action === 'approve' ? 'approve' : 'reject';
      await apiFetch(`/api/wfh/${approveDialog.req.id}/${ep}`, {
        method: 'POST', body: JSON.stringify({ note: approveDialog.note }),
      });
      setApproveDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const cancel = async (id: string) => {
    try { await apiFetch(`/api/wfh/${id}/cancel`, { method: 'POST' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const savePolicy = async () => {
    if (!policyDialog) return;
    try { await apiFetch('/api/wfh/policy', { method: 'POST', body: JSON.stringify(policyDialog) }); setPolicyDialog(null); load(); }
    catch (e: any) { setError(e.message); }
  };

  const tabs = [
    { label: 'My WFH' },
    { label: `Team Today (${todayData.length})` },
    { label: 'Week View' },
    ...(isManager ? [{ label: `Pending (${allRequests.length})` }] : []),
    ...(isHR ? [{ label: 'Policy' }] : []),
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeWork sx={{ fontSize: 32, color: '#1b5e20' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Work From Home</Typography>
            <Typography variant="body2" color="text.secondary">Request, track, and manage hybrid work days</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1b5e20' }}
          onClick={() => setRequestDialog({ date: today, session: 'full_day', reason: '', location: 'Home' })}>
          Request WFH
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'WFH Today', value: stats.todayWfh, color: '#1b5e20' },
            { label: 'This Week', value: stats.thisWeekWfh, color: '#0277bd' },
            { label: 'Pending Approval', value: stats.pendingApprovals, color: '#e65100' },
            { label: 'This Month', value: stats.thisMonthTotal, color: '#6a1b9a' },
          ].map(s => (
            <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
              <Card sx={{ textAlign: 'center', p: 1 }}>
                <CardContent sx={{ pb: '8px !important' }}>
                  <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Policy info banner */}
      {policy && (
        <Alert icon={<Laptop />} severity="info" sx={{ mb: 2 }}>
          <strong>WFH Policy:</strong> Max {policy.maxDaysPerWeek} days/week · {policy.advanceNoticeDays} day advance notice required
          {policy.autoApprove ? ' · Auto-approved within quota' : ' · Manager approval required'}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          {tabs.map((t, i) => <Tab key={i} label={t.label} />)}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ── MY WFH ── */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Session</b></TableCell>
                <TableCell><b>Location</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Note</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myRequests.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell><Typography fontWeight={600}>{fmtDate(r.date)}</Typography></TableCell>
                  <TableCell>{SESSION_LABELS[r.session]}</TableCell>
                  <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Home sx={{ fontSize: 14 }} />{r.location}</Box></TableCell>
                  <TableCell><Chip label={STATUS_CFG[r.status]?.label} size="small" color={STATUS_CFG[r.status]?.color} /></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.approverNote ?? r.reason ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(r.status === 'pending' || r.status === 'approved') && r.date >= today && (
                      <Tooltip title="Cancel">
                        <IconButton size="small" color="error" onClick={() => cancel(r.id)}><Cancel fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {myRequests.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  No WFH requests yet. Click "Request WFH" to get started.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── TEAM TODAY ── */}
      {tab === 1 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            🏠 {todayData.length} people working from home today ({fmtDate(today)})
          </Typography>
          {todayData.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>Everyone's in the office today 🏢</Paper>
          ) : (
            <Grid container spacing={1.5}>
              {todayData.map(r => (
                <Grid key={r.id} size={{ xs: 6, sm: 4, md: 3 }}>
                  <Paper sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: '#1b5e20', fontSize: '0.85rem' }}>
                      {r.employee?.firstName?.[0]}{r.employee?.lastName?.[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>{r.employee?.firstName} {r.employee?.lastName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.employee?.department?.name ?? '—'} · {SESSION_LABELS[r.session]}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* ── WEEK VIEW ── */}
      {tab === 2 && (
        <Box>
          <Grid container spacing={1.5}>
            {weekData.map(day => (
              <Grid key={day.date} size={{ xs: 12, sm: 6, md: 2.4 as any }}>
                <Paper sx={{ p: 1.5, borderTop: day.date === today ? '3px solid #1b5e20' : undefined }}>
                  <Typography variant="subtitle2" fontWeight={700} color={day.date === today ? '#1b5e20' : undefined}>
                    {fmtDate(day.date)}
                    {day.date === today && <Chip label="Today" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: '#1b5e20', color: '#fff' }} />}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color="#1b5e20" sx={{ my: 0.5 }}>{day.count}</Typography>
                  <Typography variant="caption" color="text.secondary">working remotely</Typography>
                  {day.requests.slice(0, 4).map(r => (
                    <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: '#e8f5e9', color: '#1b5e20' }}>
                        {r.employee?.firstName?.[0]}
                      </Avatar>
                      <Typography variant="caption" noWrap>{r.employee?.firstName} {r.employee?.lastName?.[0]}.</Typography>
                    </Box>
                  ))}
                  {day.count > 4 && <Typography variant="caption" color="text.secondary">+{day.count - 4} more</Typography>}
                  {day.count === 0 && <Typography variant="caption" color="text.secondary">All in office</Typography>}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ── PENDING APPROVALS ── */}
      {isManager && tab === 3 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fff8e1' }}>
                <TableCell><b>Employee</b></TableCell>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Session</b></TableCell>
                <TableCell><b>Location</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allRequests.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{r.employee?.firstName} {r.employee?.lastName}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.employee?.department?.name}</Typography>
                  </TableCell>
                  <TableCell>{fmtDate(r.date)}</TableCell>
                  <TableCell>{SESSION_LABELS[r.session]}</TableCell>
                  <TableCell>{r.location}</TableCell>
                  <TableCell><Typography variant="caption">{r.reason || '—'}</Typography></TableCell>
                  <TableCell>
                    <Button size="small" color="success" startIcon={<CheckCircle />}
                      onClick={() => setApproveDialog({ req: r, note: '', action: 'approve' })}>
                      Approve
                    </Button>
                    <Button size="small" color="error" startIcon={<Cancel />}
                      onClick={() => setApproveDialog({ req: r, note: '', action: 'reject' })}>
                      Reject
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {allRequests.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                  <CheckCircle sx={{ color: '#2e7d32', verticalAlign: 'middle', mr: 0.5 }} />No pending approvals
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── POLICY (HR) ── */}
      {isHR && tab === (isManager ? 4 : 3) && policy && (
        <Paper sx={{ p: 3, maxWidth: 500 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Global WFH Policy</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color="text.secondary">Max days/week</Typography>
              <Typography variant="h5" fontWeight={700} color="#1b5e20">{policy.maxDaysPerWeek}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color="text.secondary">Max consecutive days</Typography>
              <Typography variant="h5" fontWeight={700}>{policy.maxConsecutiveDays}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color="text.secondary">Advance notice (days)</Typography>
              <Typography variant="h5" fontWeight={700}>{policy.advanceNoticeDays}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color="text.secondary">Auto-approve</Typography>
              <Typography variant="h5" fontWeight={700}>{policy.autoApprove ? '✅ Yes' : '❌ No'}</Typography>
            </Grid>
          </Grid>
          {policy.notes && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{policy.notes}</Typography>}
          <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setPolicyDialog({ ...policy })}>Edit Policy</Button>
        </Paper>
      )}

      {/* ── REQUEST DIALOG ── */}
      <Dialog open={!!requestDialog} onClose={() => setRequestDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Request WFH Day
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setRequestDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={requestDialog?.date ?? today} inputProps={{ min: today }}
              onChange={e => setRequestDialog(p => p ? { ...p, date: e.target.value } : null)} />
            <TextField select label="Session" fullWidth value={requestDialog?.session ?? 'full_day'}
              onChange={e => setRequestDialog(p => p ? { ...p, session: e.target.value } : null)}>
              {Object.entries(SESSION_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            <TextField label="Location" fullWidth value={requestDialog?.location ?? 'Home'}
              onChange={e => setRequestDialog(p => p ? { ...p, location: e.target.value } : null)}
              placeholder="e.g. Home, Café, Co-working space" />
            <TextField label="Reason (optional)" fullWidth multiline rows={2} value={requestDialog?.reason ?? ''}
              onChange={e => setRequestDialog(p => p ? { ...p, reason: e.target.value } : null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1b5e20' }} onClick={submitRequest}>Submit</Button>
        </DialogActions>
      </Dialog>

      {/* ── APPROVE/REJECT DIALOG ── */}
      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {approveDialog?.action === 'approve' ? '✅ Approve WFH' : '❌ Reject WFH'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setApproveDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            {approveDialog?.req.employee?.firstName} {approveDialog?.req.employee?.lastName} — {fmtDate(approveDialog?.req.date)} ({SESSION_LABELS[approveDialog?.req.session ?? 'full_day']})
          </Typography>
          <TextField label="Note (optional)" fullWidth multiline rows={2}
            value={approveDialog?.note ?? ''}
            onChange={e => setApproveDialog(p => p ? { ...p, note: e.target.value } : null)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>Cancel</Button>
          <Button variant="contained"
            color={approveDialog?.action === 'approve' ? 'success' : 'error'}
            onClick={handleApproval}>
            {approveDialog?.action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── POLICY EDIT DIALOG ── */}
      <Dialog open={!!policyDialog} onClose={() => setPolicyDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit WFH Policy
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setPolicyDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Max days per week" type="number" fullWidth
              value={policyDialog?.maxDaysPerWeek ?? 2}
              onChange={e => setPolicyDialog(p => ({ ...p, maxDaysPerWeek: Number(e.target.value) }))} />
            <TextField label="Max consecutive days" type="number" fullWidth
              value={policyDialog?.maxConsecutiveDays ?? 3}
              onChange={e => setPolicyDialog(p => ({ ...p, maxConsecutiveDays: Number(e.target.value) }))} />
            <TextField label="Advance notice (days)" type="number" fullWidth
              value={policyDialog?.advanceNoticeDays ?? 1}
              onChange={e => setPolicyDialog(p => ({ ...p, advanceNoticeDays: Number(e.target.value) }))} />
            <TextField select label="Auto-approve" fullWidth
              value={policyDialog?.autoApprove ? 'yes' : 'no'}
              onChange={e => setPolicyDialog(p => ({ ...p, autoApprove: e.target.value === 'yes' }))}>
              <MenuItem value="no">No — requires manager approval</MenuItem>
              <MenuItem value="yes">Yes — auto-approve within quota</MenuItem>
            </TextField>
            <TextField label="Notes" fullWidth multiline rows={2} value={policyDialog?.notes ?? ''}
              onChange={e => setPolicyDialog(p => ({ ...p, notes: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={{ bgcolor: '#1b5e20' }} onClick={savePolicy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
